import React from "react";
import { observer } from "mobx-react-lite";
import BaseFormPage from "../../components/BaseFormPage";
import FormHeader from "../../components/FormHeader";
import BaseInput from "../../components/BaseInput";
import BaseTextArea from "../../components/BaseTextArea";
import useController from "./controller";
import BaseButton from "../../components/BaseButton";
import "./style.scss";

import {
  GoogleReCaptchaProvider,
  GoogleReCaptcha,
} from "react-google-recaptcha-v3";
import config from "../../config";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import SupportSendedModal from "../../modals/SupportSendedModal";
import BurgerMenu from "../../components/BurgerMenu";
import useAuth from "../../hooks/useAuth";
import PersonalAccount from "../../store/PersonalAccount";
import ROUTES from "../../utils/routes.js";

export default observer(function SupportPage() {
  const { token } = useAuth();
  // 23.10: писать в поддержку могут только вошедшие пользователи. Гостю вместо
  // формы предлагаем войти или зарегистрироваться (с подтверждением почты)
  return token ? <SupportForm /> : <SupportLoginRequired />;
});

function SupportLoginRequired() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  return (
    <div className="support-page">
      <BaseFormPage onNavigate={() => navigate(-1)}>
        <FormHeader
          titleClass="support-page__title"
          title={t("support_page.title")}
        />
        <p className="support-page__login-required">
          {t("support_page.login_required")}
        </p>
        <div className="support-page__login-actions">
          <BaseButton to={ROUTES.LOGIN_PAGE}>{t("support_page.login")}</BaseButton>
          <BaseButton unfilledGradient to={ROUTES.REGISTER_PAGE}>
            {t("support_page.register")}
          </BaseButton>
        </div>
      </BaseFormPage>
    </div>
  );
}

const SupportForm = observer(function SupportForm() {
  const {
    supportFormData,
    onSubmit,
    isFormSending,
    isMessageSended,
    isValidMessage,
    isTooLongMessage,
    handleChange,
    handleVerify,
    isValidTextInput,
    isSendError,
    refreshReCaptcha,
  } = useController();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const accountEmail = PersonalAccount.user?.email;
  return (
    <GoogleReCaptchaProvider reCaptchaKey={config.reCaptchaKey} useEnterprise>

      <div className="support-page">
        <SupportSendedModal />
        <BaseFormPage
          onNavigate={() => navigate(-1)}
          renderActionFormButton={() => (
            <BurgerMenu className="support-page__burger" />
          )}
        >
          <FormHeader
            titleClass="support-page__title"
            title={t("support_page.title")}
          />
          <form
            target="_blank"
            className="support-page__form"
            onSubmit={onSubmit}
          >
            <BaseInput
              name="name"
              label={t("support_page.name")}
              autocomplete="off"
              id="support-name"
              value={supportFormData.name}
              onChange={handleChange}
              errorMessage={!isValidTextInput ? t("type_name") : ""}
            />
            {/* ответ придёт на почту аккаунта: бэк берёт её из профиля */}
            {accountEmail ? (
              <p className="support-page__reply-to">
                {t("support_page.reply_to", { email: accountEmail })}
              </p>
            ) : null}
            <BaseTextArea
              id="support-message"
              name="message"
              value={supportFormData.message}
              onChange={handleChange}
              className="support-page__form__textarea"
              placeholder={t("support_page.message")}
              maxlength={2048}
              minLength={5}
              errorMessage={
                !isValidMessage
                  ? t("type_message")
                  : isTooLongMessage
                  ? t("too_long_message")
                  : ""
              }
            />
            <>
              <GoogleReCaptcha
                onVerify={handleVerify}
                refreshReCaptcha={refreshReCaptcha}
              />
              <BaseButton
                className="support-page__form__btn-send"
                type="submit"
                disabled={
                  !isValidMessage ||
                  isTooLongMessage ||
                  !isValidTextInput
                }
                loading={isFormSending}
              >
                {t("send")}
              </BaseButton>
              {isSendError ? (
                <p className="support-page__send-error">
                  {t("support_page.send_error")}
                </p>
              ) : null}
              {/* Бейдж reCAPTCHA скрыт в CSS (.grecaptcha-badge) — вместо него,
                 как требует Google при скрытии бейджа, показываем текст-уведомление
                 со ссылками на политику/условия. Это же убирает «битый объект»
                 (v2-anchor), который рисовался справа при неверном ключе. */}
              <p className="support-page__recaptcha-notice">
                {t("support_page.recaptcha_notice")}{" "}
                <a
                  href="https://policies.google.com/privacy"
                  target="_blank"
                  rel="noreferrer"
                >
                  {t("support_page.recaptcha_privacy")}
                </a>{" "}
                {t("support_page.recaptcha_and")}{" "}
                <a
                  href="https://policies.google.com/terms"
                  target="_blank"
                  rel="noreferrer"
                >
                  {t("support_page.recaptcha_terms")}
                </a>
                .
              </p>
            </>
          </form>
        </BaseFormPage>
      </div>
    </GoogleReCaptchaProvider>
  );
});
